import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import patientFallbackImage from "assets/images/patient-fallback.svg";
import {
  getPatientsList,
  linkAnimalToContact,
  PatientRow,
} from "services/patients.service";
import { readContactCompleteByContactId } from "services/contacts.service";
import vgToast from "utils/toast";

type Props = {
  open: boolean;
  orgId: number;
  contactId: number;
  onClose: () => void;
  onLinked: () => void;
};

const LinkPatientDialog = ({ open, orgId, contactId, onClose, onLinked }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [allPatients, setAllPatients] = React.useState<PatientRow[]>([]);
  const [linkedIds, setLinkedIds] = React.useState<Set<string>>(new Set());
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [linkingId, setLinkingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Esc closes (unless mid-operation).
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !linkingId) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, linkingId, onClose]);

  // Load org patients + the contact's already-linked patients in parallel.
  React.useEffect(() => {
    if (!open) return;
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const [allRes, linkedRes] = await Promise.all([
          getPatientsList(token, orgId),
          readContactCompleteByContactId(token, orgId, contactId),
        ]);
        if (cancelled) return;

        if (allRes.error) {
          setError(allRes.error.message);
          return;
        }
        setAllPatients(allRes.data ?? []);

        const linkedRaw = linkedRes.data?.animalList ?? [];
        const ids = new Set<string>();
        linkedRaw.forEach((row) => {
          const r = row as Record<string, unknown>;
          const id = String(r.animalId ?? r.AnimalId ?? "");
          if (id) ids.add(id);
        });
        setLinkedIds(ids);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, contactId, getAccessTokenSilently]);

  // Reset search whenever the dialog re-opens.
  React.useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const handleLink = async (patientId: string) => {
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) return;
    const animalId = Number(patientId);
    if (!Number.isFinite(animalId) || animalId <= 0) return;

    setLinkingId(patientId);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await linkAnimalToContact(
        token,
        orgId,
        contactId,
        animalId
      );
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      setLinkedIds((prev) => {
        const next = new Set(prev);
        next.add(patientId);
        return next;
      });
      vgToast.success("Patient linked.");
      onLinked();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setLinkingId(null);
    }
  };

  if (!open) return null;

  const q = search.trim().toLowerCase();
  const filtered = allPatients.filter((p) => {
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q) ||
      p.type.toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
        onClick={() => {
          if (!linkingId) onClose();
        }}
      />

      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]">
        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">
            Link existing patient
          </h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Choose a patient already in your clinic to attach to this client.
          </p>
        </div>

        <div className="px-6 py-4 border-b border-slate-100">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, id or species..."
            className="w-full rounded-md border border-slate-300 bg-white text-sm px-3 py-2 placeholder:text-slate-400 focus:outline-none focus:border-vg-blue focus:ring-[3px] focus:ring-vg-blue/15 transition-colors"
          />
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="px-6 py-6 text-sm text-slate-500">
              Loading patients...
            </div>
          )}
          {error && !loading && (
            <div className="px-6 py-6 text-sm text-red-500">{error}</div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="px-6 py-6 text-sm text-slate-500">
              No patients match that search.
            </div>
          )}
          {!loading &&
            !error &&
            filtered.map((p) => {
              const isLinked = linkedIds.has(p.id);
              const isBusy = linkingId === p.id;
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 px-6 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                >
                  <img
                    src={p.image || patientFallbackImage}
                    alt=""
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.src !== patientFallbackImage)
                        img.src = patientFallbackImage;
                    }}
                    className="h-10 w-10 rounded-full object-cover border border-vg-blue/20 bg-vg-blue/[0.04] shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {p.name || `Patient #${p.id}`}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      ID {p.id} · Species {p.type}
                    </div>
                  </div>
                  {isLinked ? (
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">
                      Linked
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleLink(p.id)}
                      disabled={isBusy || !!linkingId}
                      className="rounded-md bg-vg-blue/10 text-vg-blue text-xs font-semibold px-3 py-1.5 hover:bg-vg-blue/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/30"
                    >
                      {isBusy ? "Linking…" : "Link"}
                    </button>
                  )}
                </div>
              );
            })}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <button
            type="button"
            onClick={onClose}
            disabled={!!linkingId}
            className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkPatientDialog;
